import type {
  GranularResponsiveImageProps,
  ImageSourceProps,
  ResponsiveImageSourceProps
} from '~/legacy/types';
import { getBiggestSize, getMedia, IMAGE_TYPES } from '~/legacy/utils';

const ImageSource = ({
  media,
  url,
  size,
  extension,
  preserveRatio,
  addPadding
}: ImageSourceProps) => {
  let baseUrl = preserveRatio ? `${url}_w${size}_h${size}` : `${url}_w${size}`;
  if (addPadding) baseUrl += '_pad_';

  return (
    <>
      <source
        media={media}
        srcSet={`${baseUrl}.${extension}.webp`}
        type="image/webp"
      />
      <source
        media={media}
        srcSet={`${baseUrl}.${extension}`}
        type={`image/${extension}`}
      />
    </>
  );
};

const ResponsiveImageSource = ({
  responsiveImageType,
  url,
  extension,
  preserveRatio,
  addPadding
}: ResponsiveImageSourceProps) => {
  const mediaList = getMedia(responsiveImageType);

  return (
    <>
      {mediaList.map((mediaItem, index) => {
        const { media, size } = mediaItem;

        return (
          <ImageSource
            key={index}
            media={media || ''}
            size={size}
            url={url}
            extension={extension}
            preserveRatio={preserveRatio}
            addPadding={addPadding}
          />
        );
      })}
    </>
  );
};

const GranularResponsiveImage = ({
  url,
  alt,
  title,
  responsiveImageType,
  imageStyle,
  preserveRatio,
  addPadding = false
}: GranularResponsiveImageProps) => {
  const imageType = responsiveImageType || IMAGE_TYPES.FULL_BLEED;

  const biggestSize = getBiggestSize(imageType);

  const extension = url ? url.split('.').pop() : undefined;
  const featuredUrl = extension ? url.replace(`.${extension}`, '') : undefined;

  const isAffiliateLogo = imageType === IMAGE_TYPES.AFFILIATE_LOGO;
  const isBrandLogo = imageType === IMAGE_TYPES.BRAND_LOGO;
  const isAuthorLogo = imageType === IMAGE_TYPES.AUTHOR;

  const fallbackUrl = isAffiliateLogo
    ? `${featuredUrl}_w150.${extension}`
    : isBrandLogo
      ? `${featuredUrl}_w200.${extension}`
      : isAuthorLogo
        ? `${featuredUrl}_w150_h150.${extension}`
        : preserveRatio
          ? `${featuredUrl}_w${biggestSize}_h${biggestSize}.${extension}`
          : `${featuredUrl}_w${biggestSize}.${extension}`;

  return !url || url === undefined ? (
    <></>
  ) : (
    <>
      <picture>
        <>
          {isAffiliateLogo ? (
            <>
              <source
                srcSet={`${featuredUrl}_w150.${extension}.webp`}
                type="image/webp"
              />
              <source
                srcSet={`${featuredUrl}_w150.${extension}`}
                type={`image/${extension}`}
              />
            </>
          ) : isBrandLogo ? (
            <>
              <source
                srcSet={`${featuredUrl}_w200.${extension}.webp`}
                type="image/webp"
              />
              <source
                srcSet={`${featuredUrl}_w200.${extension}`}
                type={`image/${extension}`}
              />
            </>
          ) : isAuthorLogo ? (
            <>
              <source
                srcSet={`${featuredUrl}_w150_h150.${extension}.webp`}
                type="image/webp"
              />
              <source
                srcSet={`${featuredUrl}_w150_h150.${extension}`}
                type={`image/${extension}`}
              />
            </>
          ) : (
            <ResponsiveImageSource
              responsiveImageType={imageType}
              url={featuredUrl || ''}
              extension={extension || ''}
              preserveRatio={preserveRatio}
              addPadding={addPadding}
            />
          )}
        </>
        <img
          src={fallbackUrl}
          alt={alt}
          style={imageStyle || {}}
          loading="lazy"
          {...(title ? { title: title } : {})}
        />
      </picture>
    </>
  );
};

export default GranularResponsiveImage;
